import {
    Alert,
    AlertDescription,
    AlertIcon,
    AlertTitle,
    Box,
    Spinner,
    Switch,
} from '@chakra-ui/react';
import { useContext, useMemo } from 'react';
import SessionContext from '../../../contexts/SessionContext';
import type { EnabledNotificationTypesDict } from '../../../hooks/useGetProfile';
import type { NotificationChannel, NotificationType } from '../../../hooks/useGetNotifications';
import PartBayButton from '../../../components/PartBayButton';
import {
    SETTINGS_TABLE_GUTTER,
    SettingsActionBar,
    SettingsCard,
    SettingsPageActions,
    SettingsRow,
} from '../../../components/settings/primitives';
import { useProfileDraft } from './useProfileDraft';
import {
    dictsEqual,
    resolvePrefs,
    visibleNotificationCategories,
} from './notificationCatalog';
import NotificationPrefsTable from './NotificationPrefsTable';

const prefsEqual = (
    a: EnabledNotificationTypesDict | undefined,
    b: EnabledNotificationTypesDict | undefined,
    invoicingCapable: boolean,
    position?: string,
) =>
    dictsEqual(
        resolvePrefs(a, { invoicingCapable, position }),
        resolvePrefs(b, { invoicingCapable, position }),
    );

const NotificationsSection = () => {
    const session = useContext(SessionContext);
    const { profile, isLoading, isError, error, isDirty, isSaving, setField, save, discard } =
        useProfileDraft();

    const invoicingEnabled = session?.isAuthenticated === true && session.invoicingEnabled === true;
    const invoicingCapable =
        session?.isAuthenticated === true && session.permissions.includes('invoicing');
    const rekeyingEnabled = session?.isAuthenticated === true && session.rekeyingEnabled === true;

    const position = profile?.position;
    const prefs = useMemo(
        () => resolvePrefs(profile?.enabledNotificationTypes, { invoicingCapable, position }),
        [profile?.enabledNotificationTypes, invoicingCapable, position],
    );

    const visibleCategories = useMemo(
        () =>
            visibleNotificationCategories({
                position: profile?.position,
                managementSystem: profile?.managementSystem,
                invoicingEnabled,
                invoicingCapable,
                rekeyingEnabled,
                organizationMode: session?.isAuthenticated === true ? session.organizationMode : undefined,
            }),
        [
            profile?.position,
            profile?.managementSystem,
            invoicingEnabled,
            invoicingCapable,
            rekeyingEnabled,
            session,
        ],
    );

    if (isLoading || !profile) {
        return (
            <Box display="flex" justifyContent="center" py={12}>
                <Spinner size="xl" />
            </Box>
        );
    }

    if (isError) {
        return (
            <Alert status="error">
                <AlertIcon />
                <AlertTitle>Error loading notifications!</AlertTitle>
                <AlertDescription>{error?.message || 'An unknown error occurred.'}</AlertDescription>
            </Alert>
        );
    }

    const isPaused = profile.notificationsEnabled === false;

    const showDailyEmail =
        profile.position === 'estimator' ||
        profile.position === 'gm' ||
        profile.position === 'central-parts' ||
        profile.position === 'parts-manager' ||
        (profile.position === 'other' && profile.lastName !== 'Server');

    const setPrefs = (next: EnabledNotificationTypesDict) =>
        setField('enabledNotificationTypes', next, (a, b) => prefsEqual(a, b, invoicingCapable, position));

    const handleChannelToggle = (
        types: NotificationType[],
        channel: NotificationChannel,
        isEnabled: boolean,
    ) => {
        const next = { ...prefs };
        for (const type of types) {
            const current = next[type] ?? [];
            next[type] = isEnabled
                ? current.includes(channel)
                    ? current
                    : [...current, channel]
                : current.filter((c) => c !== channel);
        }
        setPrefs(next);
    };

    const dailySummaryDescription =
        profile.position === 'central-parts' || profile.position === 'parts-manager'
            ? 'Daily parts summary sent at 7:30 AM, covering the locations selected in My Locations, or your assigned locations if none are selected.'
            : 'Daily parts summary sent at 7:30 AM.';

    return (
        <>
            <SettingsPageActions>
                <PartBayButton
                    size="sm"
                    variant={isPaused ? 'solid' : 'outline'}
                    colorScheme="partbay-blue"
                    text={isPaused ? 'Enable' : 'Disable'}
                    onClick={() => setField('notificationsEnabled', isPaused)}
                />
            </SettingsPageActions>

            {showDailyEmail && (
                <Box px={SETTINGS_TABLE_GUTTER} mb={4} flexShrink={0}>
                    <SettingsCard>
                        <SettingsRow
                            label="Summary Email"
                            description={dailySummaryDescription}
                            control={
                                <Switch
                                    id="summary-email-toggle"
                                    colorScheme="partbay-blue"
                                    isChecked={profile.dailyEmailEnabled}
                                    onChange={(e) =>
                                        setField('dailyEmailEnabled', e.target.checked)
                                    }
                                    aria-label="Summary Email"
                                />
                            }
                        />
                    </SettingsCard>
                </Box>
            )}

            <NotificationPrefsTable
                prefs={prefs}
                isPaused={isPaused}
                categories={visibleCategories}
                onChannelToggle={handleChannelToggle}
            />

            <SettingsActionBar
                isDirty={isDirty}
                isSaving={isSaving}
                onSave={save}
                onDiscard={discard}
            />
        </>
    );
};

export default NotificationsSection;
